import Color from "./color";
import { Gradient, ShapeSet } from "app/types/model-types"
import { ID, intboolean } from "app/types/app-types";
import { TextAlign, TextVerticalAlign } from "./textAlign";
import { PathPoint } from "./PathShape";
import { CoordsType } from "../components/shape";
import { LineCapType, LineJoinType } from "shape-edit/forms/helpers";
import { LineType } from "./TextInsideShape";
import { BrushStrokeType } from "shape-entities/components/brush-svg";
import { isNumeric, number_format } from "app/helpers/other";
import { ResizeElementType } from "shape-entities/components/draggable-rectangle";

//type:
export const SOURCE_IMAGE_TYPE = 'sourceImage'
export const CIRCLE_TYPE = 'circle'
export const ELLIPSE_TYPE = 'ellipse'
export const RECTANGLE_TYPE = 'rectangle'
export const TRIANGLE1_TYPE = 'triangle1'
export const TRIANGLE2_TYPE = 'triangle2'
export const TEXT_TYPE = 'text'
export const BITMAP_TYPE = 'bitmap'
export const IMAGE_TYPE = 'image'
export const BRUSH_TYPE = 'brush'
export const BADGE_TYPE = 'badge'
export const BADGE1_TYPE = 'badge1' //DEPRECATED
export const DYNAMIC_TEXT_TYPE = 'dynamicText'
export const TEXT_WITH_ELEMENTS_TYPE = 'textWithElements'
export const IMAGE_FROM_ELEMENTS_TYPE = 'imageFromElements'
export const CUSTOM_SET_TYPE = 'customSet'
export const EXTERNAL_IMAGE_SOURCE_TYPE = 'externalImageSource'
export const RATING_TYPE = 'rating';
export const BEZIER_TYPE = 'bezier'
export const TEXT_INSIDE_SHAPE_TYPE = 'textInsideShape'
export const CURVED_TEXT_TYPE = 'curvedText'
export const CIRCLE_TEXT_TYPE = 'circleText'
export const DASHED_FRAME_TYPE = 'dashedFrame'
export const FRAME_TYPE = 'frame'   //DEPRECATED
export const POLYGON_TYPE = 'polygon'
export const LINE_TYPE = 'line'

//text_background_type:
export const NONE_TYPE = 'none' //no background
export const RECTANGLE_EACH_LINE_TYPE = 'rectangle-each-line'   //special background type

export const TEXT_SHAPE_TYPES = [
    CIRCLE_TYPE,
    ELLIPSE_TYPE,
    RECTANGLE_TYPE,
    TRIANGLE1_TYPE,
    TRIANGLE2_TYPE,
    DYNAMIC_TEXT_TYPE,
    TEXT_TYPE,
    TEXT_WITH_ELEMENTS_TYPE,
    TEXT_INSIDE_SHAPE_TYPE,
];

export const RESIZE_TYPES_TO_CHANGE_FONTSIZE: ResizeElementType[] = ['topleft', 'topright', 'bottomleft', 'bottomright']

export const NO_TEXT_CURSOR_TYPES = [
    SOURCE_IMAGE_TYPE, DYNAMIC_TEXT_TYPE, CIRCLE_TEXT_TYPE, TEXT_WITH_ELEMENTS_TYPE, RATING_TYPE, DASHED_FRAME_TYPE, BADGE_TYPE, BADGE1_TYPE, BITMAP_TYPE,
    CUSTOM_SET_TYPE, SOURCE_IMAGE_TYPE, IMAGE_FROM_ELEMENTS_TYPE, BEZIER_TYPE, POLYGON_TYPE, LINE_TYPE, BRUSH_TYPE, EXTERNAL_IMAGE_SOURCE_TYPE
]

export type AllowedShapeTypes = 'circle' | 'circleText' | 'rectangle' | 'triangle1' | 'triangle2' | 'frame' | 'dashedFrame' |
    'badge' | 'badge1' | 'bitmap' | 'customSet' | 'sourceImage' | 'text' | 'dynamicText' | 'textWithElements' | 'textInsideShape' |
    'imageFromElements' | 'polygon' | 'bezier' | 'curvedText' | 'line' | 'brush' | 'externalImageSource' | 'rating'

export type AllowedTextBackgroundShapeTypes = "" | "none" | "ellipse" | "circle" | "rectangle" | "image" | "rectangle-each-line"

export type TextXmlElementFormatter = {
    prefix: string
    suffix: string
    decimals: number
    decimal_separator: string
    thousands_separator: string
    search: string
    replace: string
    is_regexp: boolean
}

export const initTextXmlElementFormatter: TextXmlElementFormatter = {
    prefix: null,
    suffix: null,
    decimals: null,
    decimal_separator: null,
    thousands_separator: ' ',
    search: null,
    replace: '',
    is_regexp: null,
}

export const getFormattedValue = (f: TextXmlElementFormatter, v: any): any => {
    let value = v

    if (Array.isArray(value)) {
        return value
    }

    const DEFAULT_DECIMAL_SEPARATOR = ',';

    if (f.search && value) {
        if (f.is_regexp) {
            value = value.replace(new RegExp(`${f.search}`, 'g'), `${f.replace}`);
        } else {
            value = value.replaceAll(`${f.search}`, `${f.replace}`)
        }
    }

    const isNumericValue = isNumeric(value)
    const extractedNumber: number = !isNumericValue ? value.replace(/[^\. ,\d]+/g, '').trim() : value
    let result = value
    let isNumberFormatted = false

    if (typeof f.decimals !== 'undefined' && f.decimals !== null) {
        result = number_format(extractedNumber, f.decimals, f.decimal_separator ? f.decimal_separator : DEFAULT_DECIMAL_SEPARATOR, f.thousands_separator);
        isNumberFormatted = true
    } else if (typeof f.thousands_separator !== 'undefined' && f.thousands_separator !== null) {
        result = `${extractedNumber}`.replace(new RegExp(/(\d)(?=(\d\d\d)+(?!\d))/, 'g'), `$1${f.thousands_separator}`);
        isNumberFormatted = true
    }

    if (!isNumericValue && isNumberFormatted) {
        result = value.replace(extractedNumber, result)
    }

    return `${f.prefix ? f.prefix : ''}${result}${f.suffix ? f.suffix : ''}`
}

interface Shape {
    type: AllowedShapeTypes
    is_hidden?: intboolean
    is_locked?: intboolean
    is_remove_background?: intboolean
    x: number
    y: number
    name?: string
    id?: number
    index?: string
    priority: number
    template_id?: number
    alternative_template_id?: number
    color?: Color
    background?: Color
    opacity?: number
    width?: number
    height?: number
    angle?: number
    scale?: number
    is_draggable: boolean
    is_flip?: boolean
    is_flop?: boolean
    is_dynamic?: intboolean
    is_aspect_size_ratio?: boolean
    text_direction?: 0 | 1
    text_orientation?: 0 | 1
    text_content?: string
    text_content_replaced?: string
    text_fontsize?: number
    text_fontfamily?: string
    text_color?: Color
    text_stroke_color?: Color
    text_stroke_width?: number
    text_stroke_underlayer?: boolean
    text_background_color?: Color
    text_background_type?: AllowedTextBackgroundShapeTypes
    text_xml_element?: string
    text_xml_element_formatter?: string
    image_xml_element?: string
    rating_xml_element?: string
    rating_count_xml_element?: string
    rating_from_depots?: intboolean
    rating_max_value?: number | null
    rating_hide_empty?: intboolean | null
    rating_min_stars?: number | null
    star_symbol?: number | null
    text_overflow?: number | null
    text_decoration?: number | null
    text_decoration_color?: string | null
    text_decoration_linethickness?: number | null
    text_decoration_lineoverlap?: number | null
    text_wrap?: boolean
    letter_case?: number
    text_align?: TextAlign
    text_valign?: TextVerticalAlign
    text_padding?: number
    external_image_prompt?: string
    line_spacing?: number
    letter_spacing?: number
    dash_length?: number
    dash_density?: number
    stroke_width?: number
    stroke_color?: Color
    line_join?: LineJoinType
    line_cap?: LineCapType
    set?: ShapeSet
    set_id?: ID
    set_file_id?: ID
    image_file_id?: ID
    custom_set_id?: ID
    custom_set_file_id?: ID
    gradient_id?: ID
    gradient?: Gradient
    imageBlob?: string
    file?: string
    corner_radius?: number
    shadow_color?: string
    shadow_opacity?: number
    shadow_sigma?: number
    shadow_xoffset?: number
    shadow_yoffset?: number
    points?: (PathPoint | CoordsType | any)[]
    brush_strokes?: BrushStrokeType[] | string
    lines?: LineType[] | string
    clip_mask?: any
    invert_clip_mask?: boolean
    has_reflection?: boolean
    reflection_angle?: number
    reflection_offset?: number
    reflection_opacity?: number
    reflection_flip?: boolean
}

export default Shape
